import { api } from "../lib";
import type { AdditionalStainRequestSurface } from "./stainRequest.service";
import type { StagedBlockDraft } from "./stagedBlockRequest";

export interface ReflexPanelProposal {
  proposalKey: string;
  enrollmentId: string;
  reflexRuleId: string;
  specimenId: string;
  specimenNumber: string;
  matchingIcdCodes: string[];
  targetPanel: {
    id: string;
    name: string;
    versionId: string;
    components: Array<{
      stainCode: string;
      quantity: number;
    }>;
  };
}

export interface TissueCypherSignOutProposal {
  proposalKey: string;
  specimenId: string;
  specimenNumber: string;
  matchingIcdCodes: string[];
  requiresExplicitSpecimenSelection: boolean;
  targetPanel: ReflexPanelProposal["targetPanel"];
}

export interface AcceptReflexProposalInput {
  proposalKey: string;
  enrollmentId: string;
  reflexRuleId: string;
  specimenId: string;
}

export interface CasePanelSummaryCpt {
  id: string;
  code: string;
  description: string;
}

export interface CasePanelSummaryComponent {
  id: string;
  blockId: string;
  blockNumber: string;
  stainId: string;
  stainCode: string;
  quantity: number;
  billingPolicy: "standard" | "none" | "override";
  resolvedCpt: CasePanelSummaryCpt | null;
  completedSlides: number;
  totalSlides: number;
}

export interface CasePanelSummaryOrder {
  id: string;
  panelName: string;
  versionNumber: number;
  workflowKind: "ordinary" | "tissue_cypher";
  source: "accessioning" | "reflex" | "pathologist_review";
  status: "ordered" | "in_progress" | "completed";
  orderedAt: string;
  billingMode: "group_only" | "group_plus_individual" | "individual_only";
  groupCpt: CasePanelSummaryCpt | null;
  operationalChildCase: {
    id: string;
    caseNumber: string;
    status: string;
  } | null;
  affectedBlocks: Array<{ id: string; number: string }>;
  components: CasePanelSummaryComponent[];
}

export interface CasePanelSpecimenSummary {
  specimenId: string;
  specimenNumber: string;
  orders: CasePanelSummaryOrder[];
}

export interface CreatePathologistPanelInput {
  specimenId: string;
  blockIds: string[];
  stagedBlockKeys?: string[];
  stagedBlocks?: StagedBlockDraft[];
  panelDefinitionId: string;
  panelDefinitionVersionId: string;
  idempotencyKey: string;
}

export class PanelWorkflowRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PanelWorkflowRequestError";
    Object.setPrototypeOf(this, PanelWorkflowRequestError.prototype);
  }
}

function getResponseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  return (error as { response?: { status?: number } }).response?.status;
}

function translatePanelWorkflowError(
  error: unknown
): PanelWorkflowRequestError {
  const status = getResponseStatus(error);
  if (status === 403) {
    return new PanelWorkflowRequestError(
      "You do not have permission to order or review panels on this case."
    );
  }
  if (status === 404) {
    return new PanelWorkflowRequestError(
      "The case, specimen, or panel is no longer available. Refresh the case and try again."
    );
  }
  if (status === 409) {
    return new PanelWorkflowRequestError(
      "The case or panel changed in another session. Refresh the case and review the panel again."
    );
  }
  if (status === 400 || status === 422) {
    return new PanelWorkflowRequestError(
      "The panel request is no longer valid. Review the specimen and panel, then try again."
    );
  }
  if (status === undefined) {
    return new PanelWorkflowRequestError(
      "The connection was interrupted. Check the case before trying again."
    );
  }
  return new PanelWorkflowRequestError(
    "Panels are unavailable right now. Refresh the case and try again."
  );
}

async function translateRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    throw translatePanelWorkflowError(error);
  }
}

export function getPanelWorkflowErrorMessage(error: unknown): string {
  return error instanceof PanelWorkflowRequestError
    ? error.message
    : "Panels are unavailable right now. Refresh the case and try again.";
}

export const reflexPanelsService = {
  getProposals: (caseId: string) =>
    translateRequest(
      api.get<ReflexPanelProposal[]>(
        `/workflows/pathologist-review/${caseId}/reflex-proposals`
      )
    ),
  acceptProposals: (caseId: string, proposals: AcceptReflexProposalInput[]) =>
    translateRequest(
      api.post<{ createdPanelOrderIds: string[] }>(
        `/workflows/pathologist-review/${caseId}/reflex-proposals/accept`,
        { proposals }
      )
    ),
  getTissueCypherProposals: (caseId: string, draftId?: string) =>
    translateRequest(
      api.get<TissueCypherSignOutProposal[]>(
        `/workflows/pathologist-review/${caseId}/tissue-cypher-proposals`,
        draftId ? { params: { draftId } } : undefined
      )
    ),
  acceptTissueCypherProposals: (
    caseId: string,
    proposalKeys: string[],
    draftId?: string,
    explicitSpecimenSelectionConfirmed = false
  ) =>
    translateRequest(
      api.post<{ createdPanelOrderIds: string[] }>(
        `/workflows/pathologist-review/${caseId}/tissue-cypher-proposals/accept`,
        { proposalKeys, draftId, explicitSpecimenSelectionConfirmed }
      )
    ),
  getCasePanels: (caseId: string) =>
    translateRequest(
      api.get<CasePanelSpecimenSummary[]>(
        `/workflows/pathologist-review/${caseId}/panels`
      )
    ),
  createPathologistPanel: (
    caseId: string,
    input: CreatePathologistPanelInput,
    surface: AdditionalStainRequestSurface = "pathologist-review"
  ) =>
    translateRequest(
      api.post<{ createdPanelOrderId: string }>(
        `/workflows/${surface}/${caseId}/panels`,
        input
      )
    ),
};
